import React, { useState, useEffect } from 'react'
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
} from '@/components/ui-compat'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import PriceBreakdown, { GRID_MODE } from './PriceBreakdown'
import { PriceBreakdownContext } from './TenderForm'
import { PriceBreakdownRow } from '../../../reactgrid/PriceBreakdownGrid/types/PriceBreakdownRow'
import { CellChange } from '@silevis/reactgrid'
import { numberFormat } from '../../../utils'
import { gql } from 'apollo-boost'
import { FileManager } from '../../../components/file-manager'
import { Mutation, Query } from '../../../api'

const CREATE_BID_MANUALLY = gql`
  mutation createBidManually(
    $tenderId: ID!
    $bidderId: ID!
    $round: Int!
    $data: Json
    $totalPrice: Int
  ) {
    createBidManually(
      tenderId: $tenderId
      bidderId: $bidderId
      round: $round
      data: $data
      totalPrice: $totalPrice
    ) {
      id
      bidder {
        id
        name
      }
      totalPrice
      round
      createdAt
      updatedAt
    }
  }
`

interface Props {
  isOpen: boolean
  toggle: () => void
  tender: any
  bidders: any[]
  currentRound: number
  onBidCreated?: () => void
  preselectedBidderId?: string
}

const mapFromChildren = (children: any[]) =>
  children.map(({ parentId, ...rest }) => ({
    ...rest,
    expanded: false,
    ...(parentId ? { parentId } : {}),
  }))

const CreateBidModal = ({
  isOpen,
  toggle,
  tender,
  bidders,
  currentRound,
  onBidCreated,
  preselectedBidderId,
}: Props) => {
  const { t } = useTranslation()

  const [selectedBidderId, setSelectedBidderId] = useState<string>(preselectedBidderId || '')
  const [round, setRound] = useState<number>(currentRound)
  const [priceRows, setPriceRows] = useState<PriceBreakdownRow[]>([])
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<string[]>([])
  const [createdBidId, setCreatedBidId] = useState<string | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])

  const [setBidFiles] = useMutation(Mutation.SET_BID_FILES)

  // Query files from directory to display them in the FileManager
  const fileDirectory = selectedBidderId ? `bidder/${selectedBidderId}/tender/${tender.id}/round/${round}` : null
  const { data: filesData, refetch: refetchFiles } = useQuery(Query.FILES, {
    variables: { directory: fileDirectory },
    skip: !fileDirectory,
  })

  const [createBidManually, { loading }] = useMutation(CREATE_BID_MANUALLY, {
    onCompleted: async (data) => {
      const bidId = data.createBidManually.id

      // If files were selected, attach them to the bid
      if (selectedFiles.length > 0) {
        try {
          await setBidFiles({
            variables: {
              id: bidId,
              files: selectedFiles,
            },
          })
        } catch (error) {
          console.error('Error attaching files to bid:', error)
          toastr.warning('Warning', 'Bid created but some files could not be attached')
        }
      }

      toastr.success('', t('Tender_form_create_bid_success'))
      if (onBidCreated) {
        onBidCreated()
      }
      handleClose()
    },
    onError: (error) => {
      toastr.error('Error', error.message)
    },
  })

  useEffect(() => {
    if (preselectedBidderId) {
      setSelectedBidderId(preselectedBidderId)
    }
  }, [preselectedBidderId])

  useEffect(() => {
    setRound(currentRound)
  }, [currentRound])

  useEffect(() => {
    if (tender?.enablePriceBreakdown) {
      // Find price breakdown for the selected round
      const pb = tender.priceBreakdowns?.find((pb: any) => pb.tenderRound === round)
      if (pb && pb.children) {
        setPriceRows(mapFromChildren(pb.children))
      }
    }
  }, [tender, round])

  const handleClose = () => {
    setSelectedBidderId(preselectedBidderId || '')
    setRound(currentRound)
    setPriceRows([])
    setSelectedFiles([])
    setCreatedBidId(null)
    setUploadedFiles([])
    setConfirmModalOpen(false)
    toggle()
  }

  const handleChanges = (changes: CellChange[]) => {
    let newPriceRows = [...priceRows]
    changes.forEach((change) => {
      let idx = newPriceRows.findIndex((row) => row.rowId === change.rowId)
      if (idx !== -1) {
        // @ts-ignore
        newPriceRows[idx] = { ...newPriceRows[idx], [change.columnId]: change.newCell.value }
      }
    })
    setPriceRows(newPriceRows)
  }

  // Wrapper function that matches the context signature
  const handleSetPriceRows = (priceRowsOrCallback: PriceBreakdownRow[] | ((rows: PriceBreakdownRow[]) => PriceBreakdownRow[])): PriceBreakdownRow[] => {
    if (typeof priceRowsOrCallback === 'function') {
      const newRows = priceRowsOrCallback(priceRows)
      setPriceRows(newRows)
      return newRows
    } else {
      setPriceRows(priceRowsOrCallback)
      return priceRowsOrCallback
    }
  }

  const totalPrice = tender?.enablePriceBreakdown
    ? priceRows.reduce((acc, curr) => (curr.price ? curr.price : 0) + acc, 0)
    : 0

  // Check if bid already exists for selected bidder and round
  const existingBid = tender?.bids?.find(
    (bid: any) => bid.bidder?.id === selectedBidderId && bid.round === round
  )
  const isUpdating = !!existingBid

  const handleSubmit = async () => {
    const variables: any = {
      tenderId: tender.id,
      bidderId: selectedBidderId,
      round: round,
    }

    if (tender.enablePriceBreakdown) {
      variables.data = priceRows
      variables.totalPrice = totalPrice
    } else {
      variables.data = null
      variables.totalPrice = null
    }

    await createBidManually({ variables })
  }

  const handlePrimaryAction = () => {
    if (tender.enablePriceBreakdown) {
      // Show confirmation modal if price breakdown is enabled
      setConfirmModalOpen(true)
    } else {
      // Submit directly if no price breakdown
      handleSubmit()
    }
  }

  const selectedBidder = bidders.find((b) => b.id === selectedBidderId)

  const isValid = selectedBidderId && round

  return (
    <>
      {/* Main Create Bid Modal */}
      <Modal isOpen={isOpen && !confirmModalOpen} toggle={handleClose} size="xl">
        <ModalHeader toggle={handleClose}>
          {isUpdating ? t('Tender_form_update_bid_modal_title') : t('Tender_form_create_bid_modal_title')}
        </ModalHeader>
        <ModalBody>
          <Form>
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label for="bidderSelect">
                  {t('Tender_form_create_bid_select_bidder')} *
                </Label>
                <Input
                  type="select"
                  id="bidderSelect"
                  value={selectedBidderId}
                  onChange={(e) => setSelectedBidderId(e.target.value)}
                  disabled={!!preselectedBidderId}
                >
                  <option value="">{t('Tender_form_create_bid_select_bidder_placeholder', 'Select bidder...')}</option>
                  {bidders.map((bidder) => (
                    <option key={bidder.id} value={bidder.id}>
                      {bidder.name} {bidder.contactName ? `(${bidder.contactName})` : ''}
                    </option>
                  ))}
                </Input>
              </div>

            {tender.status !== 'RUNNING' && (
              <Alert color="warning">
                {t('Tender_form_create_bid_warning_not_running', 'Warning: This tender is not currently running. You are using admin override to create this bid.')}
              </Alert>
            )}

            {tender.enablePriceBreakdown && selectedBidderId && (
              <div className="space-y-2">
                <Label>{t('Price_breakdown_heading')}</Label>
                <PriceBreakdownContext.Provider
                  value={{
                    tenderStatus: tender.status,
                    fetching: false,
                    gridMode: GRID_MODE.DEFAULT,
                    priceRows,
                    onChange: handleChanges,
                    setPriceRows: handleSetPriceRows,
                  }}
                >
                  <PriceBreakdown />
                </PriceBreakdownContext.Provider>
                <div className="mt-3">
                  <strong>{t('tender_summary_col_total')}</strong>{' '}
                  {numberFormat.format(totalPrice)}
                </div>
              </div>
            )}

            {selectedBidderId && (
              <div className="space-y-3">
                <Label className="text-base font-medium">{t('Tender_form_documents', 'Documents')}</Label>
                <FileManager
                  readOnly={false}
                  files={filesData?.files || []}
                  directory={`bidder/${selectedBidderId}/tender/${tender.id}/round/${round}`}
                  uploadDirectory={`bidder/${selectedBidderId}/tender/${tender.id}/round/${round}`}
                  showFileSelect={false}
                  onUploaded={() => {
                    // Refetch files to show newly uploaded ones
                    refetchFiles()
                  }}
                  onSelected={(fileIds) => {
                    // fileIds is the complete array: [...existingFiles, ...newFiles]
                    setSelectedFiles(fileIds)
                  }}
                  onRemoved={(removedFileIds) => {
                    // Remove the files that were deleted
                    setSelectedFiles(selectedFiles.filter((f) => !removedFileIds.includes(f)))
                  }}
                />
              </div>
            )}
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose}>
            {t('Global_cancel')}
          </Button>
          <Button
            color="primary"
            onClick={handlePrimaryAction}
            disabled={!isValid || loading}
          >
            {loading
              ? t('Tender_form_saving', 'Saving...')
              : isUpdating
                ? t('Tender_form_update_bid_button')
                : t('Tender_form_create_bid_button')
            }
          </Button>
        </ModalFooter>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(false)}>
        <ModalHeader toggle={() => setConfirmModalOpen(false)}>
          {isUpdating ? t('Tender_form_update_bid_confirm_title') : t('Tender_form_create_bid_confirm_title')}
        </ModalHeader>
        <ModalBody>
          <p>
            {isUpdating
              ? t('Tender_form_update_bid_confirm_message', {
                  bidderName: selectedBidder?.name || '',
                  totalPrice: numberFormat.format(totalPrice),
                })
              : t('Tender_form_create_bid_confirm_message', {
                  bidderName: selectedBidder?.name || '',
                  totalPrice: numberFormat.format(totalPrice),
                })
            }
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModalOpen(false)}>
            {t('Global_cancel')}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={loading}>
            {loading
              ? t('Tender_form_saving', 'Saving...')
              : isUpdating
                ? t('Tender_form_update_bid_button')
                : t('Tender_form_create_bid_button')
            }
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CreateBidModal
